<template>
  <div>
    <div class="h-full">
      <div
        v-clickOutside="cancelEdit"
        class="group-scope group block h-full min-h-120 md:min-h-150 w-full
               md:transition-color md:duration-300 relative
               pt-17 pb-8 cursor-pointer rounded-10 text-app-color"
        :class="[
          shadowClasses,
          { 'border-3 border-link-color': !isBookInstructionAccepted },
          isDarkMode ? 'bg-book' : 'bg-white'
        ]"
      >
        <div class="h-3 w-6/7 absolute top-0 left-1/2 transform -translate-x-1/2 rounded-2" :style="`background-color: ${bookColor.main}`" />
        <div class="h-full">
          <div class="flex pl-15 pr-20 md:pl-23 md:pr-23 w-full">
            <BookIcon :book="book" icon-size="w-7/10 h-7/10" class="mb-5" />

            <div class="flex-grow" @click="goToBook" />
          </div>
          <div class="flex flex-col h-full pl-15 pr-10 md:pl-23 md:pr-23 w-full" @click="goToBook">
            <div
              v-if="!isEdit.title"
              class="font-Inter mb-4 text-15 md:text-17 mt-5 leading-small font-bold pr-30"
            >
              {{ localBook.title }}
            </div>
            <div v-show="isEdit.title" class="w-full" @click.stop>
              <input
                ref="titleInputRef"
                v-model="localBook.title"
                class="font-Inter w-full h-36 rounded text-15 md:text-17 mt-5 mb-4 bg-hover-book outline-none font-bold"
                maxlength="30"
                type="text"
                @input="edit"
                @keypress.enter="cancelEdit"
              >
            </div>

            <p
              v-if="!isEdit.subTitle"
              class="text-13 md:text-14 mb-7 leading-normal opacity-70 group-hover:opacity-100 transition-color duration-300"
              :class="[{'opacity-100': menuIsOpen || showDropdown || showProjectsDropdown},
                       isDarkMode ? 'text-grey-250' : 'text-grey-975']"
            >
              {{ localBook.subTitle }}
            </p>
            <div v-else class="w-full" @click.stop>
              <textarea
                v-model="localBook.subTitle"
                class="w-full h-full rounded text-13 md:text-14 bg-hover-book outline-none"
                maxlength="76"
                rows="3"
                @input="edit"
                @keypress.enter="cancelEdit"
              />
            </div>
            <div style="height: 32px" />
          </div>
          <div class="absolute top-0 right-0 px-10 pb-10 pt-8 pr-10">
            <DropdownModule
              ref="menuRef"
              :icon="`${menuIsOpen || showDropdown || showProjectsDropdown ? 'opacity-100' : 'opacity-25'} ${dropdownIconClasses}`"
              :icon-has-hover="false"
              class="text-app-color text-15"
              :custom-classes="menuHoverBg"
              :width="180"
              append-to-body
              remove-optional-top-gap
              :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
              is-auto-menu-position
              @click="toggleMenu(true)"
              @close="toggleMenu(false)"
            >
              <div @click.stop>
                <div class="hover:bg-selection rounded-t-10 group-scope">
                  <ButtonModule
                    text="Rename"
                    :prefix="`icon-edit mr-15 text-main-color ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                    class="text-app-color px-12 py-11 w-full"
                    @click="toggleFullEdit"
                  />
                </div>
                <div class="hover:bg-selection group-scope">
                  <ButtonModule
                    text="Delete"
                    :prefix="`icon-delete mr-15 text-main-color ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                    class="text-app-color px-12 py-11 w-full"
                    @click="deleteBook"
                  />
                </div>

                <ColorPicker :book="book" @close="menuRef.close()" />

                <div v-if="isTeamAccount" class="hover:bg-selection group-scope rounded-b-10">
                  <ButtonModule
                    text="Invite"
                    :prefix="`icon-supervisor-account mr-15 text-main-color ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                    class="text-app-color px-12 py-11 w-full"
                    @click="inviteModalRef.openInviteModal()"
                  />
                </div>
              </div>
            </DropdownModule>
          </div>
          <div
            class="group-hover:opacity-100 absolute bottom-9"
            :class="[
              book.favorite ? 'md:opacity-100' : 'md:opacity-0',
              isTeamAccount ? 'right-45' : 'right-4'
            ]"
          >
            <ButtonModule
              :prefix="`icon-star text-24 ${book.favorite ? 'text-yellow-700' : 'text-grey-250'}`"
              class="text-app-color p-5 pr-10 rounded-6 anim-opacity"
              :has-hover="!book.favorite"
              tooltip-text="Favorite"
              tooltip-position="bottom-30 left-1/2 transform -translate-x-1/2 z-20"
              tooltip-classes="text-12 font-medium px-10 py-4 rounded-4 capitalize"
              tooltip-bg-opacity="bg-opacity-80"
              @click="toggleFavorite"
            />
          </div>
          <SharedWith
            v-if="isTeamAccount"
            ref="inviteModalRef"
            class="absolute bottom-10 right-10"
            :members="book.shareWith"
            :book="book"
            :menu-is-open="menuIsOpen"
            :is-show-dropdown="showProjectsDropdown"
            width="w-250"
            @show-dropdown="showDropdown = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'

import useBooks from './useBooks'

import ColorPicker from '@/components/books/ColorPicker'
import SharedWith from '@/components/books/SharedWith'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import BookIcon from '@/components/books/BookIcon'

export default defineComponent({
  name: 'Card',

  components: { ColorPicker, ButtonModule, DropdownModule, SharedWith, BookIcon },

  props: {
    book: Object
  },

  emits: ['delete', 'update:modelValue'],

  setup (props, { emit }) {
    const showProjectsDropdown = ref(false)
    const {
      // data
      showDropdown,
      menuIsOpen,
      localBook,
      isEdit,
      // refs
      inviteModalRef,
      menuRef,
      titleInputRef,
      // computed
      isBookInstructionAccepted,
      dropdownIconClasses,
      isTeamAccount,
      isDarkMode,
      projects,
      isTwilightTheme,
      menuHoverBg,
      bookColor,
      // function
      toggleFullEdit,
      goToBook,
      edit,
      cancelEdit,
      toggleMenu,
      deleteBook,
      openInviteModal,
      toggleFavorite
    } = useBooks({ props, emit })

    const shadowClasses = computed(() => {
      if (isDarkMode.value) {
        return menuIsOpen.value || showDropdown.value || showProjectsDropdown.value ? 'bg-hover-book' : 'bg-book hover:bg-hover-book'
      } else {
        return menuIsOpen.value || showDropdown.value || showProjectsDropdown.value ? 'shadow-book-hover' : 'shadow-book hover:shadow-book-hover'
      }
    })

    return {
      // data
      showDropdown,
      menuIsOpen,
      localBook,
      isEdit,
      // refs
      inviteModalRef,
      menuRef,
      titleInputRef,
      showProjectsDropdown,
      // computed
      shadowClasses,
      isBookInstructionAccepted,
      dropdownIconClasses,
      isTeamAccount,
      isDarkMode,
      projects,
      isTwilightTheme,
      menuHoverBg,
      bookColor,
      // function
      toggleFullEdit,
      goToBook,
      edit,
      cancelEdit,
      toggleMenu,
      deleteBook,
      openInviteModal,
      toggleFavorite
    }
  }
})
</script>
